/**
 * Runtime split-frontend tenant credentials — not baked into the build.
 * Populated by third-party onboarding (or one-time launch params in dev).
 * In bundled Jester mode this is usually unset.
 */

const SESSION_KEY = 'jester_frontend_tenant_v1';
const LOCAL_KEY = 'jester_frontend_tenant_persist_v1';
const LEGACY_SESSION_KEY = 'jester_clod_tenant_v1';
const LEGACY_LOCAL_KEY = 'jester_clod_tenant_persist_v1';

export type FrontEndTenantCredentials = {
  tenantId: string;
  tenantKey: string;
};

function normalizeTenantId(raw: unknown): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
}

function readFromStorage(storage: Storage, key: string): FrontEndTenantCredentials | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const tenantId = normalizeTenantId(parsed?.tenantId);
    const tenantKey = String(parsed?.tenantKey || '').trim();
    if (tenantId && tenantKey.length >= 8) {
      return { tenantId, tenantKey };
    }
  } catch {}
  return null;
}

function readStored(): FrontEndTenantCredentials | null {
  try {
    for (const key of [SESSION_KEY, LOCAL_KEY, LEGACY_SESSION_KEY, LEGACY_LOCAL_KEY]) {
      const storage = key === SESSION_KEY || key === LEGACY_SESSION_KEY ? sessionStorage : localStorage;
      const creds = readFromStorage(storage, key);
      if (creds) return creds;
    }
  } catch {}
  return null;
}

export function getFrontEndTenantCredentials(): FrontEndTenantCredentials | null {
  return readStored();
}

export function isFrontEndTenantConfigured(): boolean {
  return getFrontEndTenantCredentials() != null;
}

/**
 * Persist tenant credentials after third-party onboarding completes.
 * @param persist When true, survives tab close (localStorage). Default session-only.
 */
export function setFrontEndTenantCredentials(
  tenantId: string,
  tenantKey: string,
  opts?: { persist?: boolean },
): void {
  const id = normalizeTenantId(tenantId);
  const key = String(tenantKey || '').trim();
  if (!id || key.length < 8) return;
  const payload = JSON.stringify({ tenantId: id, tenantKey: key });
  try {
    sessionStorage.setItem(SESSION_KEY, payload);
    if (opts?.persist) {
      localStorage.setItem(LOCAL_KEY, payload);
    }
  } catch {}
}

export function clearFrontEndTenantCredentials(): void {
  try {
    for (const key of [SESSION_KEY, LOCAL_KEY, LEGACY_SESSION_KEY, LEGACY_LOCAL_KEY]) {
      const storage = key === SESSION_KEY || key === LEGACY_SESSION_KEY ? sessionStorage : localStorage;
      storage.removeItem(key);
    }
  } catch {}
}

const LAUNCH_PARAM_ID = 'frontEndTenantId';
const LAUNCH_PARAM_KEY = 'frontEndTenantKey';

/**
 * Dev / deep-link bootstrap: read tenant id+key from URL once, store, optionally strip from address bar.
 * Production third-party onboarding should call {@link setFrontEndTenantCredentials} instead.
 */
export function seedFrontEndTenantFromLaunchParams(opts?: { stripFromUrl?: boolean }): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const tenantId = normalizeTenantId(
      params.get(LAUNCH_PARAM_ID) ??
        params.get('clodTenantId') ??
        params.get('front_end_tenant_id') ??
        params.get('clod_tenant_id'),
    );
    const tenantKey = String(
      params.get(LAUNCH_PARAM_KEY) ??
        params.get('clodTenantKey') ??
        params.get('front_end_tenant_key') ??
        params.get('clod_tenant_key') ??
        '',
    ).trim();
    if (!tenantId || tenantKey.length < 8) return false;

    const persist =
      params.get('frontEndTenantPersist') === '1' || params.get('clodTenantPersist') === '1';
    setFrontEndTenantCredentials(tenantId, tenantKey, { persist });

    if (opts?.stripFromUrl !== false) {
      for (const name of [
        LAUNCH_PARAM_ID,
        LAUNCH_PARAM_KEY,
        'frontEndTenantPersist',
        'clodTenantId',
        'clodTenantKey',
        'clodTenantPersist',
        'front_end_tenant_id',
        'front_end_tenant_key',
        'clod_tenant_id',
        'clod_tenant_key',
      ]) {
        params.delete(name);
      }
      const qs = params.toString();
      const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', next);
    }
    return true;
  } catch {
    return false;
  }
}
